import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { Outlet } from 'react-router-dom';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { FixtureSelector } from 'components/fixture/FixtureSelector';
import { UserFixtureResultsHeader } from 'components/profile/UserFixtureResultsHeader';
import { NewRewardsBanner } from 'components/rewards/NewRewardsBanner';
import { getFixtureQueryVariables } from 'lib/so5';

import type {
  ProHistoryFixtureLayoutQuery,
  ProHistoryFixtureLayoutQueryVariables,
} from './__generated__/index.graphql';

const PRO_HISTORY_FIXTURE_LAYOUT_QUERY = gql`
  query ProHistoryFixtureLayoutQuery(
    $type: So5State
    $userSlug: String!
    $fixtureSlug: String
    $sport: Sport!
  ) {
    user(slug: $userSlug) {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        background {
          id
          pictureUrl
        }
      }
      ...UserFixtureResultsHeader_user
    }
    currentUser {
      slug
      unclaimedSo5Rewards(sport: $sport) {
        id
        slug
        ...NewRewardsBanner_so5Reward
      }
    }
    so5 {
      so5Fixture(slug: $fixtureSlug, type: $type, sport: $sport) {
        slug
        displayName
        userFixtureResults(userSlug: $userSlug) {
          slug
          totalRewardConfigs {
            id
            ...UserFixtureResultsHeader_anyRewardConfigInterface
          }
        }
        ...FixtureSelector_so5Fixture
        ...UserFixtureResultsHeader_so5Fixture
      }
    }
  }
  ${NewRewardsBanner.fragments.so5Reward}
  ${FixtureSelector.fragments.so5Fixture}
  ${UserFixtureResultsHeader.fragments.user}
  ${UserFixtureResultsHeader.fragments.anyRewardConfigInterface}
  ${UserFixtureResultsHeader.fragments.so5Fixture}
` as TypedDocumentNode<
  ProHistoryFixtureLayoutQuery,
  ProHistoryFixtureLayoutQueryVariables
>;

export const loaderFn = <Params extends { slug: string; fixture: string }>() =>
  queryLoader<Params>(
    PRO_HISTORY_FIXTURE_LAYOUT_QUERY,
    ({ slug, fixture, sport }) => {
      const { slug: fixtureSlug, type } = getFixtureQueryVariables(fixture);
      return {
        fixtureSlug,
        type,
        userSlug: slug,
        sport,
      };
    },
    {
      preventTransitionBeforeLoaded: true,
    }
  );

type Props = {
  userSlug: string;
};
export const ProHistoryFixtureLayout = ({ userSlug }: Props) => {
  const { isMe } = useCurrentUserContext();
  const { data } = useQueryLoader<ProHistoryFixtureLayoutQuery>();
  const { currentUser, user, so5 } = data;
  const { so5Fixture } = so5 || {};
  const { generateSportContextPath } = useSportContext();

  if (!so5Fixture) {
    return null;
  }

  return (
    <Vertical
      gap={2}
      style={
        {
          '--bg': `url(${user.anyUserSportProfile?.background?.pictureUrl})`,
        } as CSSProperties
      }
    >
      <FullWidthContainer.NoPadding>
        {isMe(userSlug) && (
          <NewRewardsBanner
            so5Rewards={currentUser?.unclaimedSo5Rewards || []}
          />
        )}
        <UserFixtureResultsHeader
          so5Fixture={so5Fixture}
          user={user}
          totalRewardConfigs={
            so5Fixture?.userFixtureResults?.totalRewardConfigs || []
          }
        />
        <FixtureSelector
          disableUpcoming
          pickerSize="big"
          currentFixture={so5Fixture}
          navigateTo={fixtureSlug =>
            generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_HISTORY_FIXTURE, {
              params: {
                slug: userSlug,
                fixture: fixtureSlug,
              },
            })
          }
        />
      </FullWidthContainer.NoPadding>
      <Outlet />
    </Vertical>
  );
};
